import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {SequenceNodeOutputData} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import _findLastIndex from 'lodash/findLastIndex';
import {SequenceCardScratchpadState} from '../../scratchpad/sequenceCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';

const setRepeatCycleInputSchema = z.object({
  mode: z
    .enum(['finite', 'infinite'])
    .describe(
      "'finite' repeats the cycle a set number of times then routes to the Last output. 'infinite' repeats without end (no Last output).",
    ),
  repeatCount: z
    .number()
    .int()
    .positive()
    .min(1)
    .optional()
    .nullable()
    .describe(
      'Number of times the whole cycle repeats before routing to the Last output (only used in finite mode, minimum 1)',
    ),
  lastOutputName: z
    .string()
    .optional()
    .nullable()
    .describe('Name for the Last output (only used in finite mode, defaults to "Last Output")'),
});

export default function setRepeatCycleTool() {
  async function execute(
    params: ToolExecuteParams<typeof setRepeatCycleInputSchema, SequenceCardScratchpadState>,
  ) {
    const {mode, repeatCount, lastOutputName} = params.input;
    const validatedScratchpad = validateAgentScratchpad<SequenceCardScratchpadState>(
      params.toolUtils.agentScratchpad,
      NodeType.SEQUENCE,
    );
    const {card} = validatedScratchpad;

    if (mode === 'infinite') {
      card.maxRepeats = null;
      card.outputs = card.outputs.filter((output) => output.type !== OutputType.Last);

      return toolResult('Repeat cycle set to infinite (no end)');
    }

    const count = repeatCount ?? 1;
    card.maxRepeats = count;

    const existingLastIndex = card.outputs.findIndex((output) => output.type === OutputType.Last);

    if (existingLastIndex >= 0) {
      const lastOutput = card.outputs[existingLastIndex] as SequenceNodeOutputData;

      if (lastOutputName !== undefined && lastOutputName !== null) {
        lastOutput.name = lastOutputName;
      }

      return toolResult(
        `Repeat cycle set to finite: ${count} time(s), last output: "${lastOutput.name ?? 'Last Output'}"`,
      );
    }

    const nextId = card.outputs.length > 0 ? Math.max(...card.outputs.map((o) => o.id), 0) + 1 : 1;

    const newLastOutput: SequenceNodeOutputData = {
      id: nextId,
      nodeId: NO_NODE_ID,
      name: lastOutputName ?? 'Last Output',
      type: OutputType.Last,
      maxRepeats: 1,
    };

    const lastRegularIndex = _findLastIndex(
      card.outputs,
      (output) => output.type === OutputType.Regular,
    );

    if (lastRegularIndex === -1) {
      card.outputs.unshift(newLastOutput);
    } else {
      card.outputs.splice(lastRegularIndex + 1, 0, newLastOutput);
    }

    return toolResult(
      `Repeat cycle set to finite: ${count} time(s), last output: "${newLastOutput.name}"`,
    );
  }

  return tool<typeof setRepeatCycleInputSchema, SequenceCardScratchpadState>({
    name: V2CardAgentToolName.SetRepeatCycle,
    displayName: 'Setting repeat cycle',
    description:
      'Control how the sequence repeats. In finite mode, a Last output is added to end the cycle after a set number of repetitions. In infinite mode, the sequence loops through regular outputs without end.',
    inputSchema: setRepeatCycleInputSchema,
    execute,
  });
}
